"use client"

/**
 * Composant d'intégration des sessions partagées dans l'app principale VisualGrid
 * Gère la synchronisation bidirectionnelle entre le master et les remotes
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useSharedSession } from '@/hooks/use-shared-session';
import SessionCreator from './session-creator';
import { useLanguage } from '@/lib/i18n/language-provider';
import { useRouter } from 'next/navigation';
import {
  Share2,
  Users,
  Zap,
  Monitor,
  Smartphone,
  Wifi
} from 'lucide-react';

interface SessionIntegrationProps {
  // Configuration actuelle de l'app
  currentConfig?: any;

  // Callbacks pour synchroniser avec l'app principale
  onConfigUpdate?: (config: any) => void;
  onAnimationStateUpdate?: (isAnimating: boolean) => void;
  onSessionStateChange?: (isActive: boolean, isServer: boolean) => void;
  setSendSyncMessage?: (sendFn: (type: string, data: any) => Promise<void>) => void;

  // Options d'affichage
  className?: string;
  showInSidebar?: boolean;
  hideCreate?: boolean; // Masquer le bouton "Créer une session"
  hideJoin?: boolean;   // Masquer le bouton "Rejoindre une session"

  // Auto-join depuis URL
  autoJoinCode?: string | null; // Code de session pour rejoindre automatiquement
}

export default function SessionIntegration({
  currentConfig,
  onConfigUpdate,
  onAnimationStateUpdate,
  onSessionStateChange,
  setSendSyncMessage,
  className,
  showInSidebar = false,
  hideCreate = false,
  hideJoin = false,
  autoJoinCode = null
}: SessionIntegrationProps) {
  const { t } = useLanguage();
  const router = useRouter();
  // Timestamp du dernier message reçu — toute config mise à jour dans ce délai est ignorée pour éviter les boucles
  const lastReceivedTimestampRef = useRef(0);
  const RECEIVE_COOLDOWN_MS = 500;
  // Flag pour savoir si on a déjà reçu la config initiale du HOST
  const hasReceivedHostConfigRef = useRef(false);
  // ID de la dernière config reçue (pour confirmer avec ACK)
  const lastReceivedConfigIdRef = useRef<string | null>(null);

  const sharedSession = useSharedSession({
    onConfigReceived: (receivedConfig) => {
      console.log('📥 [SESSION_INTEGRATION] Config reçue, mise à jour de l\'app', Object.keys(receivedConfig));

      // Tracker si c'est une config complète du HOST (CONFIG_SYNC)
      if (receivedConfig.CONFIG_SYNC) {
        hasReceivedHostConfigRef.current = true;
        lastReceivedTimestampRef.current = Date.now();

        // Envoyer un ACK au host pour confirmer la réception
        if (isSessionRemote && sendSyncMessage) {
          const configId = Math.random().toString(36).substring(7);
          lastReceivedConfigIdRef.current = configId;
          sendSyncMessage('CONFIG_ACK', { configId });
        }
      }

      // Marquer le timestamp pour toute config volumineuse reçue (anti-boucle)
      if (Object.keys(receivedConfig).length > 5 && !receivedConfig.SELFTEST_NAVIGATE && !receivedConfig.SELFTEST_TOGGLE_CYAN && !receivedConfig.SELFTEST_NAVIGATE_QUADRANT) {
        lastReceivedTimestampRef.current = Date.now();
      }

      // Traiter le message d'animation - clé directe isAnimating (quand c'est le seul message)
      if (receivedConfig.isAnimating !== undefined && Object.keys(receivedConfig).length === 1) {
        console.log('🎬 [SESSION_INTEGRATION] État d\'animation reçu du host (clé directe):', receivedConfig.isAnimating);
        console.log('🎬 [SESSION_INTEGRATION] onAnimationStateUpdate disponible?', !!onAnimationStateUpdate);
        if (onAnimationStateUpdate) {
          console.log('🎬 [SESSION_INTEGRATION] Appel onAnimationStateUpdate avec:', receivedConfig.isAnimating);
          onAnimationStateUpdate(receivedConfig.isAnimating);
        }
        // Passer aussi à onConfigUpdate pour que handleConfigUpdate le traite
      }

      // Traiter le message ANIMATION_STATE format ancien
      if (receivedConfig.ANIMATION_STATE) {
        console.log('🎬 [SESSION_INTEGRATION] État d\'animation reçu du host (format ANIMATION_STATE):', receivedConfig.ANIMATION_STATE.isAnimating);
        console.log('🎬 [SESSION_INTEGRATION] onAnimationStateUpdate disponible?', !!onAnimationStateUpdate);
        if (onAnimationStateUpdate) {
          console.log('🎬 [SESSION_INTEGRATION] Appel onAnimationStateUpdate avec:', receivedConfig.ANIMATION_STATE.isAnimating);
          onAnimationStateUpdate(receivedConfig.ANIMATION_STATE.isAnimating);
        }
      }

      onConfigUpdate?.(receivedConfig);
    },

    onAnimationStateReceived: (isAnimating) => {
      console.log('🎬 [SESSION_INTEGRATION] État animation reçu via callback:', isAnimating);
      // Notifier l'app principale du changement d'état d'animation
      if (onAnimationStateUpdate) {
        onAnimationStateUpdate(isAnimating);
      }
    },

    onSessionEnd: () => {
      console.log('🔚 [SESSION_INTEGRATION] Session terminée');
      onSessionStateChange?.(false, false);
      toast.info('Session partagée terminée');

      // 👉 Rediriger vers la page de base quand la session se termine (déconnexion automatique)
      setTimeout(() => {
        // Vérifier si on est sur la page remote (mobile)
        if (typeof window !== 'undefined' && window.location.pathname.includes('/remote')) {
          router.push('/grid/remote');
        } else {
          router.push('/grid');
        }
      }, 1000); // Délai pour afficher le toast
    },

    onDeviceJoined: (device) => {
      toast.success(t("toast.deviceJoined", { name: device.name }), {
        description: `Type: ${device.type === 'mobile' ? '📱 Mobile' : '🖥️ Desktop'}`
      });

      // 📤 Envoyer la config du HOST à la remote qui vient de se connecter
      // Cette synchronisation ne se fait qu'une seule fois par connexion
      if (isSessionServer && currentConfig) {
        console.log('📤 [SESSION_INTEGRATION] Envoi config du host à la remote qui se connecte');
        setTimeout(() => {
          sendSyncMessage('CONFIG_SYNC', currentConfig);
        }, 500);
      }
    },

    onDeviceLeft: (deviceId) => {
      toast.info('Un appareil a quitté la session');
    }
  });

  const {
    activeSession,
    isSessionActive,
    isSessionServer,
    isSessionRemote,
    sessionManager,
    updateConfig,
    sendSyncMessage,
    endSession,
    connectedDevices,
    isDeviceConnected
  } = sharedSession;

  // Passer la fonction sendSyncMessage au hook parent
  useEffect(() => {
    if (setSendSyncMessage && sendSyncMessage) {
      setSendSyncMessage(sendSyncMessage);
    }
  }, [setSendSyncMessage, sendSyncMessage]);

  // Synchroniser les changements de config locale vers la session (remote → host uniquement)
  // Compare via JSON pour ne pas re-envoyer quand seule la référence objet change
  const lastSentConfigJsonRef = useRef<string>('');

  useEffect(() => {
    if (!isSessionActive || !isSessionRemote || !currentConfig) return;

    // Anti-boucle : ignorer si on vient de recevoir une config
    if (Date.now() - lastReceivedTimestampRef.current < RECEIVE_COOLDOWN_MS) return;

    // Comparer par valeur, pas par référence
    const configJson = JSON.stringify(currentConfig);
    if (configJson === lastSentConfigJsonRef.current) return;

    lastSentConfigJsonRef.current = configJson;
    updateConfig(currentConfig);
  }, [currentConfig, isSessionActive, isSessionRemote, updateConfig]);

  // Notifier les changements d'état de session
  useEffect(() => {
    onSessionStateChange?.(isSessionActive, isSessionServer);
  }, [isSessionActive, isSessionServer, onSessionStateChange]);

  // ⚠️ NOTE: La config du HOST n'est envoyée qu'une seule fois dans onDeviceJoined
  // Plus d'envoi en continu pour éviter de surcharger les remotes avec la config complète

  // Sessions locales supprimées - fonctionnalité désactivée

  // Fonction pour synchroniser des messages spécifiques
  const syncSpecificMessage = useCallback(async (type: string, data: any) => {
    if (isSessionActive && isSessionRemote) {
      await sendSyncMessage(type, data);
    }
  }, [isSessionActive, isSessionRemote, sendSyncMessage]);

  // Interface pour la barre latérale (compacte)
  if (showInSidebar) {
    return (
      <div className={className}>
        {isSessionActive ? (
          <Card className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                  Session Active
                </span>
                {/* Voyant de connexion - logique adaptée */}
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${
                    isSessionActive 
                      ? 'bg-green-500 animate-pulse' 
                      : 'bg-gray-400'
                  }`}></div>
                  {isSessionServer && isDeviceConnected && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      {connectedDevices.length}
                    </span>
                  )}
                </div>
                <div className="ml-auto">
                  <Badge variant="outline" className="text-xs font-mono font-bold border-green-400 text-green-700 dark:border-green-600 dark:text-green-300">
                    {activeSession?.code}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                {isSessionRemote ? (
                  <>
                    <Smartphone className="w-3 h-3" />
                    <span>Vous contrôlez</span>
                  </>
                ) : isSessionServer ? (
                  <>
                    <Monitor className="w-3 h-3" />
                    <span>Mode affichage</span>
                  </>
                ) : (
                  <span>Rôle inconnu</span>
                )}
              </div>
              
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-4">
              <Button 
                variant="outline" 
                className="w-full text-xs h-8"
                onClick={() => {
                  // Ouvrir le modal de création de session
                  // (à implémenter selon votre système de modals)
                }}
              >
                <Share2 className="w-3 h-3 mr-1" />
                Partager
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Interface complète
  return (
    <div className={className}>
      {/* Indicateur de session active */}
      {isSessionActive && (
        <Card className="mb-4 border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-green-800 dark:text-green-200">
              <Zap className="w-4 h-4" />
              Session Partagée Active
              {/* Code de session - Cloud uniquement */}
              <div className="ml-auto relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg blur-sm opacity-75 animate-pulse"></div>
                <div className="relative bg-gradient-to-r from-green-500 to-blue-600 p-[2px] rounded-lg">
                  <div className="bg-white dark:bg-gray-900 rounded-md px-3 py-1">
                    <span className="font-mono font-black text-lg text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600 tracking-wider">
                      {activeSession?.code}
                    </span>
                  </div>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pt-0">
            <div className="flex items-center justify-between text-sm mb-3">
              <div className="flex items-center gap-2">
                {isSessionRemote ? (
                  <>
                    <Smartphone className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 dark:text-green-300">
                      Vous contrôlez cette session
                    </span>
                  </>
                ) : isSessionServer ? (
                  <>
                    <Monitor className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 dark:text-green-300">
                      Mode affichage
                    </span>
                  </>
                ) : (
                  <span>Rôle inconnu</span>
                )}
              </div>
              
              <div className="flex items-center gap-1 text-green-600">
                <Wifi className="w-3 h-3" />
                <span className="text-xs">
                  Cloud
                </span>
              </div>
            </div>
            
            {/* Voyant de connexion - logique adaptée selon le rôle */}
            <div className="flex items-center gap-2 p-2 bg-white dark:bg-green-900 rounded-lg border border-green-200 dark:border-green-700">
              <div className={`w-3 h-3 rounded-full ${
                isSessionActive
                  ? 'bg-green-500 animate-pulse shadow-lg' 
                  : 'bg-gray-400'
              }`}></div>
              <span className="text-sm text-green-800 dark:text-green-200 font-medium">
                {isSessionRemote ? (
                  // Sur Remote (téléphone) : on confirme qu'on est connecté
                  'Connecté comme remote'
                ) : isSessionServer ? (
                  // Sur Server (PC) : on affiche les remotes connectées
                  isDeviceConnected 
                    ? `${connectedDevices.length} remote${connectedDevices.length > 1 ? 's' : ''} connectée${connectedDevices.length > 1 ? 's' : ''}` 
                    : 'En attente de connexion remote...'
                ) : (
                  'Session active'
                )}
              </span>
              {((isSessionServer && isDeviceConnected) || isSessionRemote) && (
                <Users className="w-4 h-4 text-green-600 ml-auto" />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions pour sessions locales supprimées - uniquement Cloud disponible */}

      {/* Boutons de contrôle de session - Affichés quand la session est active */}
      {isSessionActive && (
        <div className="space-y-3 mt-4">
          <Button
            variant="destructive"
            onClick={async () => {
              if (sessionManager) {
                // Si on est le serveur, envoyer un message de fermeture à tous les clients
                if (isSessionServer) {
                  try {
                    await sendSyncMessage('SESSION_CLOSE', { reason: 'server_closed' });
                  } catch (error) {
                  }
                  // Attendre un peu pour laisser le temps aux clients de recevoir le message
                  await new Promise(resolve => setTimeout(resolve, 500));
                }

                await sessionManager.disconnect();
                toast.info(t('sharedSession.sessionEnded'));

                // 👉 Rediriger vers la page de base après fermeture de session
                // Pour mobile remote: /grid/remote
                // Pour host desktop: /grid
                setTimeout(() => {
                  if (isSessionRemote) {
                    router.push('/grid/remote');
                  } else {
                    router.push('/grid');
                  }
                }, 1000); // Délai pour afficher le toast
              }
            }}
            className={`w-full h-14 text-lg font-bold relative overflow-hidden ${
              isSessionServer
                ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/25'
                : 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 shadow-lg shadow-orange-500/25'
            }`}
          >
            {isSessionServer ? (
              <>
                Fermer la session
              </>
            ) : (
              <>
                Quitter la session
              </>
            )}
          </Button>
        </div>
      )}

      {/* Interface de création/gestion de session */}
      {!isSessionActive && (
        <SessionCreator
          onSessionCreated={(sessionInfo, manager) => {
            console.log('🎉 [SESSION_INTEGRATION] Session créée:', sessionInfo.code);
            (sharedSession as any).initializeSession(sessionInfo, manager);
          }}
          onSessionJoined={(sessionInfo, manager) => {
            console.log('🔗 [SESSION_INTEGRATION] Session rejointe:', sessionInfo.code);
            (sharedSession as any).initializeSession(sessionInfo, manager);
          }}
          hideCreate={hideCreate}
          hideJoin={hideJoin}
          autoJoinCode={autoJoinCode}
        />
      )}

    </div>
  );
}

/**
 * Hook pour faciliter l'utilisation de SessionIntegration
 */
export function useSessionIntegration(currentConfig: any, onAnimationStateUpdate?: (isAnimating: boolean) => void) {
  const [sessionState, setSessionState] = React.useState({
    isActive: false,
    isServer: false
  });

  // Référence pour stocker la fonction sendSyncMessage
  const sendSyncMessageRef = React.useRef<((type: string, data: any) => Promise<void>) | null>(null);

  const handleSessionStateChange = useCallback((isActive: boolean, isServer: boolean) => {
    setSessionState({ isActive, isServer });
  }, []);

  // Callback pour l'état d'animation
  const handleAnimationStateUpdate = useCallback((isAnimating: boolean) => {
    console.log('🎬 [SESSION_INTEGRATION_HOOK] Mise à jour état animation:', isAnimating);
    if (onAnimationStateUpdate) {
      onAnimationStateUpdate(isAnimating);
    }
  }, [onAnimationStateUpdate]);

  // Fonction pour enregistrer la fonction sendSyncMessage depuis le composant SessionIntegration
  const setSendSyncMessage = useCallback((sendFn: (type: string, data: any) => Promise<void>) => {
    sendSyncMessageRef.current = sendFn;
  }, []);

  // Fonction wrapper pour envoyer des messages
  const sendSyncMessage = useCallback(async (type: string, data: any) => {
    if (sendSyncMessageRef.current) {
      await sendSyncMessageRef.current(type, data);
    } else {
      // console.warn('🚫 [SESSION_INTEGRATION] sendSyncMessage non disponible');
    }
  }, []);

  // Fonction pour terminer la session (stub - sera appelée depuis app/grid/page.tsx)
  const endSession = useCallback(async () => {
    console.log('🔚 [SESSION_INTEGRATION_HOOK] endSession appelé');
    // Cette fonction est un stub, la vrai logique est dans SessionIntegration
  }, []);

  return {
    sessionState,
    handleSessionStateChange,
    isInSession: sessionState.isActive,
    isSessionServer: sessionState.isServer,
    isSessionRemote: sessionState.isActive && !sessionState.isServer,
    sendSyncMessage,
    setSendSyncMessage,
    endSession
  };
}


